import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../components/DashboardLayout";
import { FlowStepIcons } from "../components/FlowStepIcons";
import { TemplateFlowPreview } from "../components/TemplateFlowPreview";
import { TemplateTags } from "../components/TemplateTags";
import { exploreApi } from "../services/rag-service";
import { useProjects } from "../contexts/ProjectContext";

// Use Template dialog: same 2-field shape as Flows.jsx's CreateFlow dialog
// (display name + project picker), but calls exploreApi.useTemplate instead
// of a blank-flow create.
function UseTemplateDialog({ open, onClose, template, onCreated }) {
  const { t } = useTranslation();
  const { availableProjects } = useProjects();
  const isViewer = !availableProjects.some(
    (project) => project.accessLevel !== "viewer",
  );
  // AP's own first-party "use template" flow pre-fills from the template
  // FLOW's own displayName (not the template's overall title) - falls back
  // to the template's name if that's empty/generic.
  const defaultName = template?.flows?.[0]?.displayName || template?.name || "";
  const [displayName, setDisplayName] = useState(defaultName);
  const [projectId, setProjectId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDisplayName(defaultName);
      setProjectId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const flow = await exploreApi.useTemplate(template.id, {
        display_name: displayName,
        project_id: projectId,
      });
      onClose();
      onCreated(flow);
    } catch (error) {
      console.error("Error creating flow from template:", error);
      toast.error(
        error.message || t("flows.failedToUseTemplate"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("flows.useTemplateDialogTitle")}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <TextField
            label={t("flows.flowName")}
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="use-template-project-select-label">
            {t("flows.project")}
          </InputLabel>
          <Select
            labelId="use-template-project-select-label"
            label="Project"
            fullWidth
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            {availableProjects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{t("flows.projectHelper")}</FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="secondary" variant="text">
          {t("common.cancel")}
        </Button>
        <Tooltip title={isViewer ? t("flows.viewerCannotCreate") : ""}>
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isViewer || !displayName || !projectId || submitting}
            >
              {t("flows.useTemplate")}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

const TemplateDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const description = template?.description || template?.summary;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    exploreApi
      .getTemplate(templateId)
      .then((data) => {
        if (!cancelled) {
          setTemplate(data);
        }
      })
      .catch((err) => {
        console.error("Error fetching template detail:", err);
        if (!cancelled) {
          setError(err.message || t("flows.failedToLoadTemplate"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, t]);

  const handleCreated = (flow) => {
    toast.success(t("flows.templateUsedSuccess"));
    navigate(`/flows/${flow.projectId}/${flow.id}`);
  };

  return (
    <DashboardLayout
      title={template?.name || t("flows.exploreTab")}
      fullHeight={true}
      noPadding={true}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/flows", { state: { activeTab: 2 } })}
          sx={{ mb: 3, alignSelf: "flex-start" }}
        >
          {t("common.back")}
        </Button>

        {loading && (
          <Box>
            <Skeleton variant="text" width="40%" height={48} />
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {template && !loading && (
          <Paper
            sx={{
              overflow: { xs: "auto", lg: "hidden" },
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "2fr 3fr" },
                height: { xs: "auto", lg: "100%" },
              }}
            >
              <Box
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  minWidth: 0,
                  height: { xs: "auto", lg: "100%" },
                  overflow: { xs: "visible", lg: "auto" },
                }}
              >
                <Box
                  component="header"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    mb: 4,
                    gap: 3,
                  }}
                >
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {template.name}
                  </Typography>

                  <TemplateTags
                    tags={template.tags}
                    savingsOnly
                    includeTimeSavings
                  />

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => setDialogOpen(true)}
                    endIcon={<ArrowRight size={18} aria-hidden="true" />}
                    sx={{
                      minHeight: 48,
                      "& .MuiButton-endIcon svg": {
                        transform: (theme) =>
                          theme.direction === "rtl" ? "scaleX(-1)" : "none",
                      },
                    }}
                  >
                    {t("flows.useTemplate")}
                  </Button>
                </Box>

                {description && (
                  <Box
                    component="section"
                    aria-labelledby="template-about"
                    sx={{ mb: 4 }}
                  >
                    <Typography
                      id="template-about"
                      variant="h6"
                      component="h2"
                      sx={{ mb: 1.5 }}
                    >
                      {t("flows.templateAbout")}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        whiteSpace: "pre-line",
                        lineHeight: 1.75,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                )}

                <Box component="section" aria-labelledby="template-pieces">
                  <Typography
                    id="template-pieces"
                    variant="h6"
                    component="h2"
                    sx={{ mb: 1.5 }}
                  >
                    {t("flows.usedPieces")}
                  </Typography>
                  <Box sx={{ overflowX: "auto", pb: 1 }}>
                    <FlowStepIcons
                      trigger={template.flows?.[0]?.trigger}
                      maxVisible={10}
                      iconSize={32}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  height: { xs: 480, lg: "100%" },
                  borderInlineStart: { lg: "1px solid" },
                  borderTop: { xs: 1, lg: 0 },
                  borderColor: "divider",
                }}
              >
                {template.flows?.[0]?.trigger ? (
                  <TemplateFlowPreview trigger={template.flows[0].trigger} />
                ) : null}
              </Box>
            </Box>
          </Paper>
        )}

        <UseTemplateDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          template={template}
          onCreated={handleCreated}
        />
      </Box>
    </DashboardLayout>
  );
};

export default TemplateDetail;
